import { MtgLogo } from "@/components/MtgLogo";
import { useSettings } from "@/context/SettingsContext";
import { Dumbbell, HandHelping, GraduationCap, CalendarClock, ExternalLink, PlayCircle } from "lucide-react";

const PHASE_ACCENT = ["#3B82F6", "#0D9488", "#10B981"];
const PLAN_LABELS = { rehabilitation: "Rehabilitation Plan", posture: "Posture Plan", maintenance: "Maintenance Plan", treatment: "Rehabilitation Plan" };
const planLabel = (m) => PLAN_LABELS[m] || "Rehabilitation Plan";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function PlanDocument({ plan }) {
  const { settings } = useSettings();
  if (!plan) return null;
  const accent = plan.mode === "maintenance" ? "#0F766E" : "#0D9488";
  const fullLogo = settings?.logo_full_url;

  return (
    <div id="plan-document" className="mx-auto max-w-[820px] bg-white font-sans text-slate-800" data-testid="plan-document">
      {/* Header */}
      <div className="flex items-start justify-between border-b-2 pb-5" style={{ borderColor: accent }}>
        <div className="flex items-center gap-3">
          {fullLogo ? (
            <img src={fullLogo} alt={settings?.business_name || "Logo"} className="h-10 object-contain" data-testid="plan-doc-logo" />
          ) : (
            <>
              <MtgLogo className="h-7 w-7" logoUrl={settings?.logo_url} />
              <div className="font-heading text-lg font-extrabold tracking-tight" style={{ color: accent }}>{settings?.business_name || "Medical Therapy Group"}</div>
            </>
          )}
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-400">
            {planLabel(plan.mode)}
          </div>
        </div>
        <div className="text-right text-xs text-slate-500">
          <div><span className="text-slate-400">Patient:</span> <span className="font-semibold text-slate-800">{plan.patient_name}</span></div>
          <div><span className="text-slate-400">Cliniko ID:</span> <span className="font-mono">{plan.cliniko_id}</span></div>
          <div><span className="text-slate-400">Practitioner:</span> {plan.practitioner}</div>
          <div><span className="text-slate-400">Created:</span> {fmtDate(plan.created_at)}</div>
        </div>
      </div>

      <h1 className="mt-5 font-heading text-2xl font-bold text-slate-900">{plan.diagnosis || planLabel(plan.mode)}</h1>

      {/* Overview */}
      <div className="mt-4 grid grid-cols-3 gap-3 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
        <Stat label="Total Duration" value={`${plan.overview?.total_duration_weeks} wks`} />
        <Stat label="Recommended Sessions" value={plan.overview?.recommended_sessions} />
        <Stat label="Phases" value={plan.overview?.phase_names?.length || 3} />
        <div className="col-span-3 border-t border-slate-200 pt-3">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Programme phases</div>
          <ol className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {(plan.overview?.phase_names || []).map((n, i) => (
              <li key={`${i}-${n}`} className="flex items-center gap-2">
                <span className="grid h-5 w-5 place-items-center rounded-full text-[11px] font-bold text-white" style={{ background: PHASE_ACCENT[i] }}>{i + 1}</span>
                <span className="font-medium text-slate-700">{n}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Phases */}
      {(plan.phases || []).map((ph, i) => (
        <section key={ph._k || i} className="plan-phase mt-6" data-testid={`plan-doc-phase-${ph.number}`}>
          <div className="flex items-center justify-between rounded-t-lg px-4 py-2.5 text-white" style={{ background: PHASE_ACCENT[i] || accent }}>
            <div className="flex items-center gap-2.5">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-white/25 text-xs font-bold">{ph.number}</span>
              <span className="font-heading text-base font-bold">{ph.title}</span>
            </div>
            <span className="text-xs font-medium opacity-90">{ph.duration_weeks} weeks · {ph.sessions} sessions</span>
          </div>
          <div className="rounded-b-lg border border-t-0 border-slate-200 p-4">
            {ph.summary && <p className="text-sm leading-relaxed text-slate-600">{ph.summary}</p>}

            {ph.milestones?.length > 0 && (
              <div className="mt-3">
                <SectionLabel>Milestones</SectionLabel>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-slate-700">
                  {ph.milestones.map((m, k) => <li key={`${k}-${m}`}>{m}</li>)}
                </ul>
              </div>
            )}

            <div className="mt-4 grid gap-4">
              {ph.manual_therapy?.length > 0 && (
                <Block icon={HandHelping} title="Manual Therapy" accent={accent}>
                  <ul className="list-disc space-y-0.5 pl-5 text-sm text-slate-700">
                    {ph.manual_therapy.map((m, k) => <li key={`${k}-${m}`}>{m}</li>)}
                  </ul>
                </Block>
              )}

              {ph.therapeutic_exercise?.length > 0 && (
                <Block icon={Dumbbell} title="Therapeutic Exercise" accent={accent}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {ph.therapeutic_exercise.map((ex, k) => (
                      <div key={ex._k || k} className="overflow-hidden rounded-lg border border-slate-200">
                        <div className="relative h-28 w-full bg-slate-100">
                          <img src={ex.media_url} alt={ex.name} className="h-full w-full object-cover" loading="lazy" />
                          <a href={ex.source_url} target="_blank" rel="noreferrer" className="no-print absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-white/90 px-1.5 py-1 text-[10px] font-semibold text-teal-700 shadow-sm">
                            {ex.media_type === "video" ? <PlayCircle className="h-3 w-3" /> : <ExternalLink className="h-3 w-3" />} Rehab My Patient
                          </a>
                        </div>
                        <div className="p-2.5">
                          <div className="text-sm font-semibold text-slate-800">{ex.name}</div>
                          <div className="mt-1 flex flex-wrap gap-1 font-mono text-[10px] text-slate-500">
                            <Tag>{ex.sets} sets</Tag><Tag>{ex.reps} reps</Tag>
                            {ex.hold && ex.hold !== "-" && ex.hold !== "n/a" && <Tag>hold {ex.hold}</Tag>}
                            <Tag>{ex.frequency}</Tag>
                          </div>
                          {(ex.rmp_desc || ex.description) && (
                            <p className="mt-1.5 text-[11px] leading-snug text-slate-500">{ex.rmp_desc || ex.description}</p>
                          )}
                          {(ex.video_url || ex.source_url) && (
                            <a href={ex.video_url || ex.source_url} target="_blank" rel="noreferrer" className="no-print mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700" data-testid={`plan-doc-watch-${k}`}>
                              <PlayCircle className="h-3.5 w-3.5" /> Watch video
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Block>
              )}

              {ph.patient_education?.length > 0 && (
                <Block icon={GraduationCap} title="Patient Education" accent={accent}>
                  <ul className="list-disc space-y-0.5 pl-5 text-sm text-slate-700">
                    {ph.patient_education.map((m, k) => <li key={`${k}-${m}`}>{m}</li>)}
                  </ul>
                </Block>
              )}
            </div>

            <div className="mt-4 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600 ring-1 ring-slate-200">
              <CalendarClock className="h-4 w-4 text-slate-400" />
              <span className="font-medium text-slate-500">Supervised sessions:</span> {ph.supervised_frequency}
            </div>
          </div>
        </section>
      ))}

      {/* Overall summary */}
      {plan.overall_summary && (
        <section className="mt-6">
          <SectionLabel>Overall Summary</SectionLabel>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{plan.overall_summary}</p>
        </section>
      )}

      {/* References */}
      {plan.references?.length > 0 && (
        <section className="mt-6">
          <SectionLabel>References</SectionLabel>
          <ol className="mt-1 space-y-1 text-xs text-slate-500">
            {plan.references.map((r) => (
              <li key={r.id} className="flex gap-2">
                <span className="font-mono font-semibold text-teal-700">[{r.id}]</span>
                <span>{r.full} <span className="font-medium text-slate-600">({r.citation})</span></span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Footer */}
      <footer className="mt-8 flex items-center justify-between border-t border-slate-200 pt-3 text-[10px] text-slate-400">
        <span>Medical Therapy Group · Clinical Decision Support</span>
        <span>This plan is a clinician-reviewed draft. Not a substitute for in-person assessment.</span>
      </footer>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{label}</div>
    <div className="font-heading text-xl font-bold tabular-nums text-slate-900">{value}</div>
  </div>
);
const SectionLabel = ({ children }) => (
  <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{children}</div>
);
const Block = ({ icon: Icon, title, accent, children }) => (
  <div>
    <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold" style={{ color: accent }}>
      <Icon className="h-4 w-4" /> {title}
    </div>
    {children}
  </div>
);
const Tag = ({ children }) => (
  <span className="rounded bg-slate-100 px-1.5 py-0.5">{children}</span>
);
